import axios from 'axios';
import { useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const useAxiosFetch = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const id = useSelector(state => state.userInfo.id); // Redux에서 storeNo 가져오기

  const clearAuthState = useCallback(() => {
    try {
      localStorage.removeItem('persist:root');
    } catch (_) {}
  }, []);

  const refetchJwt = useCallback(async () => {
    if (!id) {
      clearAuthState();
      Swal.fire({
        icon: 'warning',
        title: '로그인 필요',
        text: '로그인이 필요합니다. 로그인 페이지로 이동합니다.',
        confirmButtonText: '확인',
      }).then(() => {
        navigate(`/login`);
      });
      return;
    }
    try {
      await axios.post(
        `${process.env.REACT_APP_API_SERVER}refresh`,
        {},
        { withCredentials: true }
      );
    } catch (e) {
      if (e.response?.data?.resultCode === 401) {
        clearAuthState();
        Swal.fire({
          icon: 'warning',
          title: '로그인 필요',
          text: '로그인이 필요합니다. 로그인 페이지로 이동합니다.',
          confirmButtonText: '확인',
        }).then(() => {
          navigate(`/login`);
        });
      } else {
        console.warn('JWT refresh failed');
      }
    }
  }, [id, navigate, clearAuthState]);

  // fetchData를 완전히 안정적인 참조로 만들기 위해 useRef 사용
  const fetchDataRef = useRef(null);

  const fetchDataImpl = useCallback(
    async (configParams, isRetry = false) => {
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_SERVER,
        withCredentials: true, // 쿠키를 요청에 포함
      });

      setIsLoading(true);
      try {
        const response = await axiosInstance(configParams);
        return response;
      } catch (error) {
        if (error.response && error.response.status === 401) {
          if (!isRetry) {
            // 최초 1회만 리프레시 시도
            await refetchJwt();
            return await fetchDataRef.current(configParams, true);
          }
          clearAuthState();
          Swal.fire({
            icon: 'warning',
            title: '로그인 필요',
            text: '로그인이 필요합니다. 로그인 페이지로 이동합니다.',
            confirmButtonText: '확인',
          }).then(() => {
            navigate(`/login`);
          });
          return undefined;
        } else {
          return error.response;
        }
      } finally {
        setIsLoading(false);
      }
    },
    [refetchJwt, clearAuthState, navigate]
  );

  // ref를 통해 항상 최신 함수 참조 유지
  fetchDataRef.current = fetchDataImpl;

  // fetchData는 항상 동일한 참조를 반환하도록 메모이제이션 (의존성 없음)
  const fetchData = useCallback(async (configParams, isRetry = false) => {
    return fetchDataRef.current(configParams, isRetry);
  }, []);

  return { isLoading, fetchData };
};

export default useAxiosFetch;
